/**
 * Operator groups for reference-kind FieldTypes.
 *
 * Subtypes inherit from their parent via the chain walk in `../operators`.
 */

import { hasAll, hasAny, hasNone } from "./array.js"
import { equals } from "./equality.js"
import type { OperatorGroup } from "./index.js"
import { isEmpty, isNotEmpty } from "./presence.js"
import { contains, endsWith, notContains, startsWith } from "./text.js"

const presence: OperatorGroup = { label: "Presence", operators: [isEmpty, isNotEmpty] }

export const operatorGroups: Record<string, OperatorGroup[]> = {
  // Root fallback for id references without their own group (record, type). The
  // chain walk lands here, so they get equality + presence rather than falling all
  // the way through to string's text operators (contains, startsWith) against an
  // id/type column.
  reference: [{ label: "Match", operators: [equals] }, presence],
  contact: [{ label: "Text", operators: [contains, notContains, startsWith, endsWith] }, presence],
  schema: [{ label: "Match", operators: [equals] }, presence],
  user: [
    {
      label: "Match",
      operators: [
        { ...equals, label: "is" },
        { ...hasAny, label: "is any of" },
      ],
    },
    presence,
  ],
  source: [presence],
  time: [presence],
  // A set of records reached through a relationship path (list → parent), like a
  // junction's targets rather than one id in a column — so the path-dependent set
  // operators, not the root's column equality.
  related: [
    {
      label: "Match",
      operators: [
        { ...hasAny, label: "is any of" },
        { ...hasAll, label: "is all of" },
        { ...hasNone, label: "is none of" },
      ],
    },
    presence,
  ],
  // record, type, parent: no own group — inherit the `reference` root above via
  // the chain walk (equality + presence).
}
